import React, { useState } from "react";
import Button from "../../ui/Button";
import ModalInput from "../../ui/ModalTextInput";
import CustomModalContainer from "../../../components/modals/CustomModalContainer";
import axiosInstance from "../../../apis/baseUrl";
import { useAppDispatch, useAppSelector } from "../../../redux/app/hooks";
import { toast } from "react-toastify";
import { changeAdminPasswordAsync } from "../../../redux/features/adminSlice";

interface ChangePasswordModalProps {
  open: boolean;
  onClose: () => void;
  onSuccess: () => void;
}

const ChangePasswordModal: React.FC<ChangePasswordModalProps> = ({
  open,
  onClose,
  onSuccess,
}) => {
  const { adminProfile, isLoading } = useAppSelector((state) => state.admin);
  const [formData, setFormData] = useState({
    otp: "",
    newPassword: "",
    hash: "",
  });
  const [otpSent, setOtpSent] = useState(false);
  const [countdown, setCountdown] = useState(0);
  const [isResendDisabled, setIsResendDisabled] = useState(false);
  const dispatch = useAppDispatch();

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
  };

  const handleSendOTP = async () => {
    try {
      const response: any = await axiosInstance.post(
        "v1/auth/send_reset_password",
        { email: adminProfile?.email }
      );

      if (response?.data) {
        toast.success(response?.data?.message);
        setFormData((prev) => ({ ...prev, hash: response?.data?.data }));
        setOtpSent(true);
        startCountdown();
      } else {
        console.error("Failed to send OTP");
      }
    } catch (error) {
      console.error("Error sending OTP:", error);
    }
  };

  const startCountdown = () => {
    setIsResendDisabled(true);
    setCountdown(60);

    const interval = setInterval(() => {
      setCountdown((prev) => {
        if (prev === 1) {
          clearInterval(interval);
          setIsResendDisabled(false);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
  };

  const validatePassword = (password: string) => {
    const regex = /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[\W_]).{8,}$/;
    return regex.test(password);
  };

  const handleChangePassword = async () => {
    if (!validatePassword(formData.newPassword)) {
      toast.error(
        "Password must be at least 8 characters long and include uppercase, lowercase, number, and special character."
      );
      return;
    }

    try {
      await dispatch(
        changeAdminPasswordAsync({
          parameters: {
            ...formData,
            email: adminProfile?.email,
          },
          isAuthenticated: true,
        })
      ).unwrap();
      onSuccess();
    } catch (err) {
      console.log(err);
    }
  };

  if (!open) return null;

  return (
    <CustomModalContainer
      title="Change Password"
      footer={
        <div className="flex justify-between relative bottom-0">
          <Button
            type="button"
            onClick={onClose}
            label="Discard"
            layout="inline" variant="ghost"
          />
          <Button
            label="Update password"
            type="submit"
            layout="inline"
            onClick={handleChangePassword}
            disabled={
              !adminProfile?.email ||
              !formData.newPassword ||
              !formData.otp ||
              !formData.hash
            }
            isLoading={isLoading}
          />
        </div>
      }
      open={open}
      onClose={onClose}
    >
      <div className="p-6">
        <form>
          <ModalInput
            label="Email"
            type="email"
            placeholder="Enter your email"
            required
            value={adminProfile?.email}
            onChange={handleInputChange}
            name="email"
          />
          <div
            className="w-full align-left text-primary cursor-pointer"
            onClick={!isResendDisabled ? handleSendOTP : undefined}
            style={{
              cursor: isResendDisabled ? "not-allowed" : "pointer",
              opacity: isResendDisabled ? 0.5 : 1,
            }}
          >
            {isResendDisabled
              ? `Resend OTP in ${countdown} seconds`
              : otpSent
              ? "Resend OTP"
              : "Send OTP"}
          </div>

          {otpSent && (
            <>
              <ModalInput
                label="New password"
                type="password"
                placeholder="Enter new password"
                required
                value={formData.newPassword}
                onChange={handleInputChange}
                name="newPassword"
              />
              {!validatePassword(formData.newPassword) &&
                formData.newPassword && (
                  <p className="text-sm text-red-500 mt-1">
                    Password must be at least 8 characters and include
                    uppercase, lowercase, number, and special character.
                  </p>
                )}
              <ModalInput
                label="OTP"
                type="text"
                placeholder="Enter OTP"
                required
                value={formData.otp}
                onChange={handleInputChange}
                name="otp"
              />
            </>
          )}
        </form>
      </div>
    </CustomModalContainer>
  );
};

export default ChangePasswordModal;
